import type {
  PaletteCommand,
  PaletteSection,
} from 'features/project/features/command-palette/types'

const SECTION_ORDER: Record<PaletteSection, number> = {
  Navigation: 0,
  Settings: 1,
  Actions: 2,
  Dashboards: 3,
  'Saved views': 4,
  Catalog: 5,
  'Switch hub': 6,
}

// Caps keep huge collections (hundreds of hubs, dashboards, themes) from
// flooding the list — typing narrows the search within each section.
const SECTION_RESULT_CAPS: Partial<Record<PaletteSection, number>> = {
  Dashboards: 5,
  'Saved views': 5,
  Catalog: 5,
  'Switch hub': 10,
}

const searchableText = (command: PaletteCommand): string =>
  [...(command.context ?? []), command.label].join(' ').toLowerCase()

const startsWord = (text: string, token: string): boolean =>
  text.split(/\s+/).some((word) => word.startsWith(token))

// Per-token score against one command. 0 means the token doesn't match at
// all, which disqualifies the whole command (every token must match).
const scoreToken = (command: PaletteCommand, token: string): number => {
  const text = searchableText(command)
  if (text.startsWith(token)) return 100
  if (startsWord(text, token)) return 50
  if (text.includes(token)) return 25
  const keywords = command.keywords ?? []
  for (const keyword of keywords) {
    const normalized = keyword.toLowerCase()
    if (normalized.startsWith(token)) return 10
    if (normalized.includes(token)) return 5
  }
  return 0
}

const scoreCommand = (command: PaletteCommand, tokens: string[]): number => {
  let total = 0
  for (const token of tokens) {
    const score = scoreToken(command, token)
    if (score === 0) return 0
    total += score
  }
  return total
}

const capPerSection = (commands: PaletteCommand[]): PaletteCommand[] => {
  const counts = new Map<PaletteSection, number>()
  return commands.filter((command) => {
    const cap = SECTION_RESULT_CAPS[command.section]
    if (cap === undefined) return true
    const count = counts.get(command.section) ?? 0
    counts.set(command.section, count + 1)
    return count < cap
  })
}

// Regroups a ranked list so each section renders contiguously (under one
// heading), with sections ordered by their best-ranked match.
const groupBySectionRelevance = (
  ranked: PaletteCommand[],
): PaletteCommand[] => {
  const sections: PaletteSection[] = []
  for (const command of ranked) {
    if (!sections.includes(command.section)) sections.push(command.section)
  }
  return sections.flatMap((section) =>
    ranked.filter((command) => command.section === section),
  )
}

/**
 * Case-insensitive ranking over context + label + keywords: full-text prefix
 * beats word-boundary prefix beats substring beats keyword match. Every
 * whitespace-separated query token must match somewhere. An empty query
 * returns the non-searchOnly catalog in section order; searchOnly (entity)
 * commands appear only in ranked results. Results are capped per section and
 * grouped so each section renders contiguously.
 */
export const filterCommands = (
  commands: PaletteCommand[],
  query: string,
): PaletteCommand[] => {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) {
    return capPerSection(
      commands
        .filter((command) => !command.searchOnly)
        .sort((a, b) => SECTION_ORDER[a.section] - SECTION_ORDER[b.section]),
    )
  }
  const ranked = commands
    .map((command) => ({ command, score: scoreCommand(command, tokens) }))
    .filter((scored) => scored.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((scored) => scored.command)

  return groupBySectionRelevance(capPerSection(ranked))
}
